import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { UploadedFileSummarySchema } from "@/domains/openapi/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";

interface UploadedFilesTableProps {
  data: UploadedFileSummarySchema[];
  onViewUploadedFile: (id: string) => void;
  pageSize: number;
}

export const UploadedFilesTable: FC<UploadedFilesTableProps> = ({
  data,
  onViewUploadedFile,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<UploadedFileSummarySchema>[]>(
    () => [
      {
        header: "ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Owner Space Account ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.owner_space_account_id} />,
      },
      {
        header: "Filename",
        cell: ({ row }) => (
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.original.normalized_filename}
          </div>
        ),
      },
      {
        header: "MIME Type",
        cell: ({ row }) => <TextFieldRow value={row.original.mime_type} />,
      },
      {
        header: "Size",
        cell: ({ row }) => (
          <TextFieldRow value={`${row.original.file_size_bytes.toLocaleString()} bytes`} />
        ),
      },
      {
        header: "Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.created_at} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewUploadedFile(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewUploadedFile]
  );

  const sortedData = useMemo(() => {
    return orderBy(data, x => DateTime.fromISO(x.created_at).toMillis(), "desc");
  }, [data]);

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
